import { CanvasObject }           from './canvas.object';
import { Line }                   from './visuals/line';
import { Track }                  from '@/models/scenario/track';
import { Port }                   from './port';
import { Logger }                 from '@/util/logger';
import { VisualFactory }          from './visuals/visual.factory';
import { OnChangedInterface }     from '@/helpers/on.changed.interface';
import { VirtualCanvas }          from './virtual.canvas';
import { EventHandlerInterface }  from './events/event.handler.interface';
import { HighLighterInterface }   from './high.lighter.interface';
import { HighLightableInterface } from './high.lightable.interface';
import { HighLightInterface }     from './high.light.interface';

/**
 *
 * @class Connection
 *
 * a route from one track to another, (anchored at the ports of
 * the top level blocks).
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class Connection
  extends CanvasObject
  implements OnChangedInterface, EventHandlerInterface, HighLightableInterface {

  /** the current highlight if there is one */
  public highLightStyle: HighLightInterface|null = null;

  /** the highlighter we can use for doing hover/click highlights */
  public highlighter: HighLighterInterface|null  = null;

  /** source track */
  public src: Track|null                         = null;

  /** destination track */
  public dst: Track|null                         = null;

  /** source port */
  public from: Port|null                         = null;

  /** target port */
  public to: Port|null                           = null;

  /** train speed supported for route */
  public speed: number                           = 0.0;

  /** color */
  public color: string                           = 'white';

  /** transparency */
  public opacity: number                         = 0.65;

  /** visibility */
  public visible: boolean                        = true;

  /** the width of the wire to the "dot" */
  public lineWidth: number                       = 5;

  /** the wire from the text label to the "dot" */
  public route: Line | null                      = null;

  /** the connection id (as it is in the JSON */
  public id: number                              = 0;

  /** the links needed by this connection (are blocked when this connection is in use) */
  public parallel: number[]                      = [];

  /** saved style when we have a style override in play */
  public savedStyle: {
    color?:   string,
    opacity?: number
  } = {};

  /**
   *
   * @param canvas      VirtualCanvas all objects must render to a canvas
   * @param highlighter The HighLightManager we can use for highlighting and style overrides
   * @param config      Partial<BlockTitle> override the style options
   *
   */
  constructor(
    canvas: VirtualCanvas,
    highlighter: HighLighterInterface|null,
    config: Partial<Connection> = {}) {

    /*
     *
     * connectors are not grouped into a parent, but we still ignore
     * global resize notification; the connection viewer will just force
     * update all connections on any window resize.
     *
     */

    super(canvas, true);

    this.highlighter = highlighter;

    /* use any overrides */

    Object.assign(this, config);

    Logger.debug(`Connection ctor() config: `, config);

    if (!this.from || !this.to) {

      Logger.error(`Connection ctor() either from or to port is missing.`);

      return ;
    }

    this.route = VisualFactory.buildLine(this.routeConfig());

    if (this.route) {

      /* save the handle, so we can use it to manipulate the visual later */

      this.route.handle = this.canvas()?.addShape(this.route, this);

      this.boundUpdate({
        x:      this.route.x      ?? 0,
        y:      this.route.y      ?? 0,
        width:  this.route.width  ?? 10,
        height: this.route.height ?? 10
      });
    }

    /* tell the from/to ports to update me when they change their position */

    this.from.registerWatcher(this);

    this.to.registerWatcher(this);

  }

  /** inform client that the source has changed */
  onChange(): void {

    /* if either port changes, re-layout. */

    this.layout();
  }

  /** generate the configuration for the title text label */
  private routeConfig(): Partial<Line> {

    let width: number;
    let height: number;
    let points: {x: number, y: number}[]  = [];
    let color: string                     = this.color;
    let lineWidth: number                 = this.lineWidth;
    let visible: boolean                  = true;
    let opacity: number                   = this.opacity;

    if (!this.from || !this.to) {

      Logger.error(`Connection routeConfig() either from or to port is missing.`);

      return {};
    }

    let x1: number = this.from.dotX;
    let y1: number = this.from.dotY;
    let x2: number = this.to.dotX;
    let y2: number = this.to.dotY;

    points.push({x: x1, y: y1});
    points.push({x: x2, y: y2});

    width     = Math.abs(x2 - x1);
    height    = Math.abs(y2 - y1);

    return {
      x: x1,
      y: y1,
      width,
      height,
      points,
      color,
      lineWidth,
      visible,
      opacity,
      lineCap: 'round'
    };
  }

  /** upon return object should have updated its layout or geometry as needed. */
  public layout(): CanvasObject {

    /*
     * the screen geometry has changed somehow, so we have to recalculate our layout and
     * then get the canvas visuals to update in place.
     *
     */

    /* rebuild the wire */

    let handle: any = this.route?.handle ?? null;

    if (!handle) {

      /* nothing to do layout on yet */

      return this;
    }

    this.route        = VisualFactory.buildLine(this.routeConfig());
    this.route.handle = handle;

    this.boundUpdate({
      x:      this.route.x      ?? 0,
      y:      this.route.y      ?? 0,
      width:  this.route.width  ?? 10,
      height: this.route.height ?? 10
    });

    this.canvas()?.updateShape(this.route);

    return this;
  }


  /** handle mouse over (hover start) */
  onMouseOver(): void {

    this.highlighter?.hovering(this);
  }

  /** handle mouse out (hover end) */
  onMouseOut(): void {

    this.highlighter?.leaving(this);
  }

  /** handle a click */
  onClick(): void {

    this.highlighter?.clicked(this);
  }

  /** handle a double click */
  onDoubleClick(): void {


  }

  /**
   * enable or disable highlighting.  When not null, set highLightStyle to
   * the new style, and object should apply any applicable style overrides.
   * When null, highLightStyle should be cleared, and the object should
   * revert to its normal styles.
   *
   * @param style the highlight to apply, or clear the current highlight.
   */
  highLight(style: HighLightInterface|null): void {

    let highlighter: HighLighterInterface|null = this.canvas()?.highlighter ?? null;

    if (!highlighter) {

      /* no highlighter to use */

      return ;
    }

    /* are we removing the override? */

    if (!style) {

      /* revert */

      if (this.savedStyle.color) {

        this.color = this.savedStyle.color;
      }

      if (this.savedStyle.opacity) {

        this.opacity = this.savedStyle.opacity;
      }

      this.savedStyle = {};

      /* update the visuals */

      this.layout();

      return ;
    }

    /* save the current styling so we can revert back to it later */

    this.savedStyle = {
      color:   this.color,
      opacity: this.opacity
    }

    if (style.color) {

      this.color = style.color;
    }

    if (style.opacity) {

      this.opacity = style.opacity;
    }

    /* update the visuals */

    this.layout();
  }

}

